import dayjs from 'dayjs';

import i18n from '@/core/i18n';

// Dayjs format constants
/** Date map keys and API params — "2026-02-11" */
export const DATE_KEY_FORMAT = 'YYYY-MM-DD';
/** Compact format for DOM element IDs and React keys — "20260211" */
export const DATE_ID_FORMAT = 'YYYYMMDD';
/** Month-year DOM IDs — "02-2026" */
export const MONTH_YEAR_ID_FORMAT = 'MM-YYYY';
/** Display month-year header — "Feb 2026" */
export const MONTH_YEAR_DISPLAY_FORMAT = 'MMM YYYY';
/** Full month name — "February" */
export const FULL_MONTH_FORMAT = 'MMMM';
/** Year — "2026" */
export const YEAR_FORMAT = 'YYYY';
/** Abbreviated weekday — "Tue" */
export const WEEKDAY_SHORT_FORMAT = 'ddd';
/** dayjs short date without year — "Feb 11" */
export const SHORT_DATE_FORMAT_DAYJS = 'MMM D';

/* Example usage
console.log(formatHourToTime(8)); // "08:00"
*/
export const formatHourToTime = (hour: number): string => {
  return dayjs().hour(hour).minute(0).format('HH:mm');
};

export const formatMinutesToTime = (minutes: number): string => {
  const totalMinutes = Math.round(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

/**
 * Converts time string (HH:mm format) to total minutes
 * @param time - Time string in HH:mm format (e.g., "08:30")
 * @returns Total minutes (e.g., 510 for "08:30")
 * @example
 * formatTimeToMinutes('08:30') // returns 510
 * formatTimeToMinutes('00:45') // returns 45
 */
export const formatTimeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/* Example usage
console.log(extractHour('08:00')); // 8
*/
export const extractHour = (time: string): number => {
  return dayjs(`1970-01-01T${time}`).hour();
};

export const getTimeDifferenceString = (date: Date | number | null | undefined): string => {
  if (!date) return '';

  const currentTime = new Date();

  const timestamp = date instanceof Date ? date.getTime() : date;

  const diffInMinutes = Math.floor((currentTime.getTime() - timestamp) / (1000 * 60));

  if (diffInMinutes < 1) return i18n.t('common:lessThanMinuteAgo');
  if (diffInMinutes < 60) return i18n.t('common:minutesAgo', { count: diffInMinutes });

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return i18n.t('common:hoursAgo', { count: diffInHours });

  const diffInDays = Math.floor(diffInHours / 24);
  return i18n.t('common:daysAgo', { count: diffInDays });
};

/**
 * Formats decimal hours into "Xh Ym/day" format string.
 * @param totalHours - Total hours as decimal (e.g., 2.5 for 2h 30m)
 * @returns Formatted string like "2h 30m" or "2h"
 */
export const formatHoursToTimeString = (totalHours: number): string => {
  let hours = Math.floor(totalHours);
  let minutes = Math.round((totalHours - hours) * 60);

  // Handle case where rounding gives us 60 minutes - convert to hours
  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }

  if (minutes === 0) {
    return i18n.t('common:timeFormat.hoursOnly', { hours });
  }
  return i18n.t('common:timeFormat.hoursAndMinutes', { hours, minutes });
};

/**
 * Converts minutes to decimal hours
 * @param minutes - Total minutes
 * @returns Decimal hours (e.g., 90 minutes = 1.5 hours)
 * @example
 * minutesToHours(90) // returns 1.5
 * minutesToHours(60) // returns 1
 */
export const minutesToHours = (minutes: number): number => {
  return minutes / 60;
};

/**
 * Converts decimal hours to minutes.
 * @returns Minutes (e.g., 1.5 hours = 90 minutes)
 * @example
 * hoursToMinutes(1.5) // returns 90
 * hoursToMinutes(8) // returns 480
 */
export const hoursToMinutes = (hours: number): number => {
  return hours * 60;
};
